/**
 * Docked inspector rail. Collapse control lives inside the rail — never a
 * floating handle outside the bar.
 */

import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface DockedRailProps {
  label: string;
  collapseLabel: string;
  expandLabel: string;
  children: React.ReactNode;
}

export const DockedRail: React.FC<DockedRailProps> = ({
  label,
  collapseLabel,
  expandLabel,
  children
}) => {
  const [open, setOpen] = useState(true);

  if (!open) {
    return (
      <aside className="shrink-0 w-8 border border-[var(--color-border)] rounded bg-[var(--color-secondary-bg)]">
        <button
          type="button"
          className="w-full min-h-[10rem] py-2 flex flex-col items-center gap-2 text-[var(--color-surface-text)] hover:opacity-90"
          aria-expanded={false}
          aria-label={expandLabel}
          title={expandLabel}
          onClick={() => setOpen(true)}
        >
          <ChevronLeftIcon className="w-4 h-4 shrink-0" aria-hidden />
          <span
            className="text-[10px] text-[var(--color-secondary-text)] whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {label}
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="relative shrink-0 w-full lg:w-56 border border-[var(--color-border)] rounded bg-[var(--color-secondary-bg)] p-2.5">
      <button
        type="button"
        className="absolute top-2 right-1.5 p-0.5 text-[var(--color-secondary-text)] hover:opacity-90"
        aria-expanded={true}
        aria-label={collapseLabel}
        title={collapseLabel}
        onClick={() => setOpen(false)}
      >
        <ChevronRightIcon className="w-4 h-4" aria-hidden />
      </button>
      <div className="pr-4">{children}</div>
    </aside>
  );
};
